import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    ArmService,
    AuthenticationService,
    DossierMedicalService,
    EspaceClientService,
    EspaceDocumentaireService,
    FoldersService
} from '@kv-common-components/kx-services';
import { DocumentListHelperService } from '@shared/services/document-list-helper.service';
import { StyleService } from '@shared/services/style.service';
import { NGXLogger } from 'ngx-logger';
import { NextStepIntroductionComponent } from '../next-step-introduction/next-step-introduction.component';
import { Appointment } from '@modules/souscription/document-medicale/document-list/appointment';

@Component({
    selector: 'app-document-list-component',
    templateUrl: './document-list.component.html',
    styleUrls: ['./document-list.component.scss']
})
export class DocumentListComponent
    extends NextStepIntroductionComponent
    implements OnInit
{
    espaceClient: any;
    documentList = [];
    nonARMDocumentList = [];
    docsNotInUserPossession = [];
    allChecked = false;
    showTextePedagogique = true;
    documentListIsLoading = false;
    folder: any;
    showFaqModal = false;
    appointment: Appointment;
    webCallBackMedical = false;
    showModaloToCancelRDVMedical = false;
    idDossierMedical: any;
    currentDossierMedical: any;
    listePieces: any[];
    documentSelectionPossession = 'no';
    isListDownload = false;
    warningListDownloaded = false;

    constructor(
        public logger: NGXLogger,
        public espaceDocService: EspaceDocumentaireService,
        public authService: AuthenticationService,
        public espCltService: EspaceClientService,
        private dossierMedicalService: DossierMedicalService,
        public armService: ArmService,
        private documentListHelper: DocumentListHelperService,
        private folderService: FoldersService,
        public styleService: StyleService,
        public router: Router
    ) {
        super(
            logger,
            espaceDocService,
            authService,
            espCltService,
            styleService,
            router
        );
    }

    ngOnInit(): void {
        // remove background style of parent class NextStepIntroductionComponent
        this.styleService.updatePageStyle(
            'remove',
            'next-step-introduction-page'
        );
        // if we came back from recap page we have data in history
        if (
            history.state &&
            history.state.data &&
            history.state.data.docsNotInUserPossession
        ) {
            this.docsNotInUserPossession =
                this.documentListHelper.sortListByAttribute(
                    history.state.data.docsNotInUserPossession,
                    'correctLibelle'
                );
        }
        this.documentListIsLoading = true;
        const idPersonne = this.authService.currentUserValue.idPersonne;
        this.espCltService
            .getEspaceClient(idPersonne)
            .subscribe((espaceClt) => {
                this.espaceClient = espaceClt;
                const idDossier = String(
                    this.authService.currentUserValue.idDossier
                );
                const idAia = String(espaceClt.personne.idAia);
                this.dossierMedicalService
                    .getIdDossierMedical(idAia, idDossier)
                    .subscribe(
                        (data) => {
                            this.idDossierMedical = data.idDossierMedical;
                            this.setDocuments(this.idDossierMedical);
                        },
                        (error) => {
                            // no dossierMedical was found
                            this.logger.info(
                                'Could not get idDossierMedical, user may not have a medical folder',
                                { error }
                            );
                            this.setDocuments();
                        }
                    );
                const personId = espaceClt.personne.idPersonne;
                const project = espaceClt.listeProjetsCredits.find(
                    (projet) =>
                        projet.idProjetCredit ===
                        this.authService.currentUserValue.idDossier
                );
                const folderId = project.listeEmprunteurs.find(
                    (emprunteur) => emprunteur.idPersonne === personId
                ).idSousDossier;
                this.listePieces = project.listeEmprunteurs.find(
                    (emp) => emp.idPersonne === espaceClt.personne.idPersonne
                ).assurance.listePieces;
                this.folderService
                    .getOrCreateFolder(
                        folderId.toString(),
                        personId,
                        'Assurance_Emprunteur'
                    )
                    .subscribe((folder) => {
                        this.folder = folder;
                    });
            });
    }

    setDocuments(idDossierMedical?: string): void {
        if (idDossierMedical) {
            this.armService
                .getDossierMedical(idDossierMedical)
                .subscribe((dossier) => {
                    this.currentDossierMedical = dossier;
                    if (dossier.webCallback) {
                        this.webCallBackMedical = true;
                        this.showTextePedagogique = false;
                    }

                    if (
                        dossier.listeRendezVousMedical &&
                        dossier.listeRendezVousMedical.length > 0
                    ) {
                        this.populateAppointmentByIdRdv(
                            idDossierMedical,
                            dossier.listeRendezVousMedical[0]
                        );
                    }
                });
        }
        this.documentListHelper
            .getMedicalDocumentsByStatus(
                this.espaceClient,
                this.authService.currentUserValue,
                'allDocsWithStatus'
            )
            .subscribe((resp) => {
                this.documentList = resp;
                this.checkAll();
                this.documentListIsLoading = false;
                this.nonARMDocumentList = this.documentList.filter(
                    (doc) => !doc.idApi
                );
                this.documentList = this.documentList.filter(
                    (doc) => doc.idApi
                );
            });
    }

    toggleTextePedagogique(): void {
        this.showTextePedagogique = !this.showTextePedagogique;
    }

    handleDocNotInUserPossession(checked: boolean, document: any): void {
        if (checked) {
            this.docsNotInUserPossession.push(document);
            this.checkAll();
        } else {
            this.docsNotInUserPossession = this.docsNotInUserPossession.filter(
                (doc) => JSON.stringify(doc) !== JSON.stringify(document)
            );
            this.allChecked = false;
        }
    }

    handleAllDocNotInUserPossession(allChecked: boolean): void {
        if (allChecked) {
            this.docsNotInUserPossession = this.documentList.filter(
                (doc) => !doc.transmis || doc.statut === 'NON_CONFORME'
            );
        } else {
            this.docsNotInUserPossession = [];
        }
    }

    docIsNotInUserPossession(document: any): boolean {
        return (
            this.docsNotInUserPossession.find((doc) =>
                this.documentEquals(doc, document)
            ) !== undefined
        );
    }

    toggleFaq() {
        this.showFaqModal = !this.showFaqModal;
    }

    navigateToNextPage(pageUrl: string): void {
        localStorage.setItem(
            'previousPageUrl',
            '/souscription/document-medicale/document-list'
        );
        this.router.navigateByUrl(pageUrl, {
            state: {
                data: {
                    documentList: this.documentList,
                    docsNotInUserPossession: this.docsNotInUserPossession
                }
            }
        });
    }

    populateAppointmentByIdRdv(idDossierMedical: string, idRdv: string): void {
        this.armService
            .getRendezVousMedical(idDossierMedical, idRdv)
            .subscribe((rdvArm) => {
                if (rdvArm && rdvArm.dateDebut) {
                    this.showTextePedagogique = false;
                    this.armService
                        .getPraticien(rdvArm.idApiPraticien)
                        .subscribe((praticienArm) => {
                            this.appointment =
                                DocumentListHelperService.createAppointmentFromArm(
                                    rdvArm,
                                    praticienArm
                                );
                        });
                }
            });
    }

    disableCheckAllCheckbox(): boolean {
        return this.documentList.every(
            (doc) =>
                doc.transmis ||
                this.filesProvided(doc) ||
                this.filesConforme(doc)
        );
    }

    toogleModalRdvMedical() {
        this.showModaloToCancelRDVMedical = !this.showModaloToCancelRDVMedical;
    }

    confirmAnnulationRDVMedicalModal() {
        this.armService
            .deleteRendezVousMedical(
                this.idDossierMedical,
                this.currentDossierMedical.listeRendezVousMedical[0]
            )
            .subscribe((response) => {
                this.toogleModalRdvMedical();
                this.appointment = null;
            });
    }

    isDocumentSelectionPossession(event: any) {
        this.documentSelectionPossession = event.target.value;
    }

    checkisListDownloaded(pageUrl: string) {
        if (this.isListDownload) {
            this.warningListDownloaded = false;
            this.navigateToNextPage(pageUrl);
        } else {
            this.warningListDownloaded = true;
        }
    }

    handleTriggerDownloadCompleted() {
        this.warningListDownloaded = false;
    }

    checkDownloadList(event: boolean) {
        this.isListDownload = event;
    }

    nextButtonStatut() {
        if (this.documentList.length === 0) {
            return false;
        }

        const isOneNotTransmit = this.documentList.find((doc) => !doc.transmis);
        if (this.nonARMDocumentList.length > 0
            && isOneNotTransmit) {
            return true;
        }

        if (this.nonARMDocumentList.length === 0
            && isOneNotTransmit) {
                return true;
            }

        if (this.nonARMDocumentList.length > 0
            && !isOneNotTransmit) {
            return false;
        }
    }

    // set allChecked to true if the user doesn't have any of the docs
    private checkAll() {
        let nonUploadedDocs = this.documentList.filter((doc) => !doc.transmis);
        nonUploadedDocs = this.documentListHelper.sortListByAttribute(
            nonUploadedDocs,
            'correctLibelle'
        );
        this.docsNotInUserPossession =
            this.documentListHelper.sortListByAttribute(
                this.docsNotInUserPossession,
                'correctLibelle'
            );
        if (
            this.documentArraysEquals(
                nonUploadedDocs,
                this.docsNotInUserPossession
            )
        ) {
            this.allChecked = true;
        }
    }

    private documentArraysEquals(arrayA: any[], arrayB: any[]): boolean {
        return (
            arrayA.length > 0 &&
            arrayA.length === arrayB.length &&
            arrayA.every((doc, index) =>
                this.documentEquals(doc, arrayB[index])
            )
        );
    }
    private documentEquals(docA: any, docB: any): boolean {
        return (
            docA.typologie === docB.typologie && docA.libelle === docB.libelle
        );
    }
}
